import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, Box, Typography, Tabs, Tab, alpha, useTheme, Avatar, Stack, Chip } from "@mui/material";
import {
  MonitorWeight as WeightIcon,
  Mood as WellbeingIcon,
  Air as RespirationIcon,
  Bloodtype as BloodSugarIcon,
  Favorite as BloodPressureIcon,
  Thermostat as TemperatureIcon,
  AirlineStops as OxygenIcon,
  Favorite as PulseIcon,
  WaterDrop as FluidIcon,
  Warning as FallsIcon,
  EmergencyShare as BowelIcon,
  Restaurant as MealsIcon,
  EventSeat as RepositionIcon,
  Timeline as AbcIcon,
  FlashOn as SeizureIcon,
  Water as UrinalysisIcon,
  MedicalServices as InfectionIcon,
  Assessment as News2Icon,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import { fetchClient } from "../../../../../api/services/Client";
import { QK } from "../../../../../base/qk";
import Weight from "./Weight/Weight";
import Respiration from "./Respiration/Respiration";
import BloodSugar from "./BloodSugar/BloodSugar";
import BloodPressure from "./BloodPressure/BloodPressure";
import Temperature from "./Temperature/Temperature";
import Oxygen from "./Oxygen/Oxygen";
import Pulse from "./Pulse/Pulse";
import FluidMonitor from "./FluidMonitor/FluidMonitor";
import Falls from "./Falls/Falls";
import Repositioning from "./Repositioning/Repositioning";
import Wellbeing from "./Wellbeing/Wellbeing";
import BowelMovement from "./BowelMovement/BowelMovement";
import AbcPatterns from "./ABCPatterns/AbcPatterns";
import SeizureChart from "./SeizureChart/SeizureChart";
import Meals from "./Meals/Meals";
import Urinalysis from "./Urinalysis/Urinalysis";
import Infection from "./Infection/Infection";
import NEWS2 from "./NEWS2/NEWS2";

const Observation = () => {
  const { id: clientID } = useParams();
  const theme = useTheme();

  const { data: clientData } = useQuery(
    [QK.client, clientID],
    () => fetchClient(clientID),
    { enabled: !!clientID }
  );

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
  };

  const initialTab = window.location.hash ? window.location.hash.substring(1) : "weight";
  const [activeTab, setActiveTab] = useState(initialTab);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    window.location.hash = newValue;
  };

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      if (hash) setActiveTab(hash);
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const tabs = [
    { value: "weight", label: "Weight", icon: <WeightIcon sx={{ fontSize: 16 }} /> },
    { value: "wellbeing", label: "Wellbeing", icon: <WellbeingIcon sx={{ fontSize: 16 }} /> },
    { value: "respiration", label: "Respiration", icon: <RespirationIcon sx={{ fontSize: 16 }} /> },
    { value: "blood-sugar", label: "Blood Sugar", icon: <BloodSugarIcon sx={{ fontSize: 16 }} /> },
    { value: "blood-pressure", label: "BP", icon: <BloodPressureIcon sx={{ fontSize: 16 }} /> },
    { value: "temperature", label: "Temperature", icon: <TemperatureIcon sx={{ fontSize: 16 }} /> },
    { value: "oxygen-saturation", label: "Oxygen", icon: <OxygenIcon sx={{ fontSize: 16 }} /> },
    { value: "pulse", label: "Pulse", icon: <PulseIcon sx={{ fontSize: 16 }} /> },
    { value: "fluid-monitor", label: "Fluid", icon: <FluidIcon sx={{ fontSize: 16 }} /> },
    { value: "falls", label: "Falls", icon: <FallsIcon sx={{ fontSize: 16 }} /> },
    { value: "bowel-movement", label: "Bowel", icon: <BowelIcon sx={{ fontSize: 16 }} /> },
    { value: "meals", label: "Meals", icon: <MealsIcon sx={{ fontSize: 16 }} /> },
    { value: "repositioning", label: "Reposition", icon: <RepositionIcon sx={{ fontSize: 16 }} /> },
    { value: "abc-patterns", label: "ABC", icon: <AbcIcon sx={{ fontSize: 16 }} /> },
    { value: "seizure-chart", label: "Seizure", icon: <SeizureIcon sx={{ fontSize: 16 }} /> },
    { value: "urinalysis", label: "Urinalysis", icon: <UrinalysisIcon sx={{ fontSize: 16 }} /> },
    { value: "infection", label: "Infection", icon: <InfectionIcon sx={{ fontSize: 16 }} /> },
    { value: "news2", label: "NEWS2", icon: <News2Icon sx={{ fontSize: 16 }} /> },
  ];

  return (
    <Box sx={{ width: "100%", pb: 3 }}>
      {/* Header Section */}
      <Card
        sx={{
          mb: 2.5,
          borderRadius: "12px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.5,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
            <Avatar
              src={clientData?.profile_photo || clientData?.image}
              alt={`${clientData?.first_name} ${clientData?.last_name}`}
              sx={{
                width: 56,
                height: 56,
                bgcolor: theme.palette.primary.main,
                fontSize: "1.3rem",
                fontWeight: 600,
                boxShadow: `0px 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              {getInitials(clientData?.first_name, clientData?.last_name)}
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ color: "#1A1A2E" }}>
                {clientData?.title} {clientData?.first_name} {clientData?.last_name}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
                <Typography variant="body2" sx={{ color: "#667085" }}>
                  <strong>NHS:</strong> {clientData?.nhs_number || "N/A"}
                </Typography>
                <Typography variant="body2" sx={{ color: "#667085" }}>
                  <strong>DOB:</strong> {clientData?.birthdate || "N/A"}
                </Typography>
                <Typography variant="body2" sx={{ color: "#667085" }}>
                  <strong>Age:</strong> {clientData?.birthdate ? new Date().getFullYear() - new Date(clientData.birthdate).getFullYear() : "N/A"}
                </Typography>
                <Chip
                  label="Observations"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.6rem",
                    fontWeight: 500,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                  }}
                />
              </Stack>
            </Box>
          </Box>
          <Box
            sx={{
              px: 2,
              py: 0.75,
              borderRadius: "20px",
              bgcolor: alpha(theme.palette.success.main, 0.08),
              display: "flex",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#10B981" }} />
            <Typography variant="body2" sx={{ color: "#039855", fontWeight: 600 }}>
              Active
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Tabs Section */}
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 2, pt: 1.5 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 44,
              "& .MuiTab-root": {
                minHeight: 40,
                minWidth: "auto",
                py: 0.75,
                px: 2,
                fontSize: "0.75rem",
                fontWeight: 500,
                textTransform: "none",
                color: "#667085",
                borderRadius: "6px",
                "&.Mui-selected": {
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                },
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              },
              "& .MuiTabs-indicator": {
                height: 2,
                borderRadius: "2px",
                bgcolor: theme.palette.primary.main,
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    {tab.icon}
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: "inherit" }}>
                      {tab.label}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ p: 2.5, pt: 2 }}>
          {activeTab === "weight" && <Weight />}
          {activeTab === "wellbeing" && <Wellbeing />}
          {activeTab === "respiration" && <Respiration />}
          {activeTab === "blood-sugar" && <BloodSugar />}
          {activeTab === "blood-pressure" && <BloodPressure />}
          {activeTab === "temperature" && <Temperature />}
          {activeTab === "oxygen-saturation" && <Oxygen />}
          {activeTab === "pulse" && <Pulse />}
          {activeTab === "fluid-monitor" && <FluidMonitor />}
          {activeTab === "falls" && <Falls />}
          {activeTab === "bowel-movement" && <BowelMovement />}
          {activeTab === "meals" && <Meals />}
          {activeTab === "repositioning" && <Repositioning />}
          {activeTab === "abc-patterns" && <AbcPatterns />}
          {activeTab === "seizure-chart" && <SeizureChart />}
          {activeTab === "urinalysis" && <Urinalysis />}
          {activeTab === "infection" && <Infection />}
          {activeTab === "news2" && <NEWS2 />}
        </Box>
      </Card>
    </Box>
  );
};

export default Observation;